import { API_HOST } from '../../config';
import { FrameAttributes, isAllowedFrameAttribute } from '../../constant';
import { WEB_SDK_SOURCE } from '../../constants';
import { TrustFrameError } from '../../errors';
import EventEmitter, { EventKey } from '../../lib/event-emitter';
import { getOrigin } from '../../lib/url';
import {
    CallActionOptions,
    CallActionResponseActionData,
    EmptyCb,
    FrameMessageCallActionResponse,
    FrameMessageType,
    FrameMessageV1,
    FrameMessageV2,
    FrameOutMessageType,
    PaymentFormSendArgs,
    PaymentFormUrl,
    PaymentFrameEventsV1,
    PaymentFrameEventsV2,
    PaymentFrameOptions,
} from '../../types';
import { getAllow } from '../../utils/allow';
import { openLink, openLinkInGo } from '../../utils';
import { randomId } from '../../utils/randomId';
import { getSandbox } from '../../utils/sandbox';
import { applePay } from '../apple-pay';
import { listenPayment } from '../listen-payment';

export class PaymentFrame<
    PaymentFrameEvents extends PaymentFrameEventsV1 | PaymentFrameEventsV2 = PaymentFrameEventsV1,
> extends EventEmitter<PaymentFrameEvents> {
    private readonly frameUrl: PaymentFormUrl;
    private readonly frameOrigin: string;
    private readonly apiHost: string;
    private readonly canUseApplePay: boolean;
    private frame: HTMLIFrameElement | undefined;
    private unlistenFrame: undefined | EmptyCb;
    private attrs: undefined | FrameAttributes;

    private onDomMount?: (frame: HTMLIFrameElement) => void;
    private onDomUnmount?: () => void;

    constructor(
        url: PaymentFormUrl,
        { apiHost = API_HOST, canUseApplePay = false, onDomMount, onDomUnmount }: PaymentFrameOptions = {},
    ) {
        super();

        this.apiHost = apiHost;
        this.canUseApplePay = canUseApplePay;
        this.frameUrl = url;
        this.frameOrigin = getOrigin(url)!;
        this.onDomMount = onDomMount;
        this.onDomUnmount = onDomUnmount;

        this.onPaymentMessage = this.onPaymentMessage.bind(this);
    }

    getInfo() {
        return {
            frameUrl: this.frameUrl,
            apiHost: this.apiHost,
            attrs: this.attrs,
            canUseApplePay: this.canUseApplePay,
        };
    }

    // can be undefined in old browsers
    // include polyfill if necessary
    get isConnected(): boolean | undefined {
        return this.frame && this.frame.isConnected;
    }

    mount(parent: string | HTMLElement, attrs?: FrameAttributes) {
        const parentElement = typeof parent === 'string' ? document.querySelector(parent) : parent;

        if (!(parentElement instanceof HTMLElement)) {
            throw new TrustFrameError(`Invalid mount target "${parent}"`);
        }

        if (this.frame) {
            throw new TrustFrameError('Frame already mounted');
        }

        this.frame = this.createFrame(attrs);

        this.attrs = attrs;

        const unListenForm = listenPayment(this.onPaymentMessage);
        const unListenApplePay = this.canUseApplePay
            ? applePay({
                  apiHost: this.apiHost,
                  onSendResponse: (m) => {
                      this.send(m);
                  },
              })
            : undefined;

        this.unlistenFrame = () => {
            unListenForm();
            unListenApplePay?.();
        };
        parentElement.appendChild(this.frame);

        if (this.onDomMount) {
            this.onDomMount(this.frame);
        }
    }

    unmount() {
        if (this.unlistenFrame) {
            this.unlistenFrame();
        }

        if (this.frame?.parentNode) {
            this.frame.parentNode.removeChild(this.frame);

            if (this.onDomUnmount) {
                this.onDomUnmount();
            }
        }
    }

    send(data: PaymentFormSendArgs): void {
        if (this.frame) {
            this.frame.contentWindow?.postMessage(data, this.frameOrigin);
        }
    }

    callAction(action: CallActionOptions): Promise<CallActionResponseActionData> {
        return new Promise((resolve) => {
            const actionId = randomId();

            const responseWatcher = (message: FrameMessageCallActionResponse): void => {
                if (message?.data?.actionId !== actionId) {
                    return;
                }

                this.off(FrameMessageType.callActionResponse, responseWatcher);
                resolve(message.data.actionData);
            };

            this.on(FrameMessageType.callActionResponse, responseWatcher);

            this.send({
                source: WEB_SDK_SOURCE,
                type: FrameOutMessageType.callAction,
                data: {
                    actionId,
                    ...action,
                },
            });
        });
    }

    private createAttributes(frame: HTMLIFrameElement, attrs?: FrameAttributes) {
        if (attrs) {
            Object.keys(attrs).forEach((name) => {
                if (isAllowedFrameAttribute(name)) {
                    const htmlAttrName = name === 'className' ? 'class' : name;
                    frame.setAttribute(htmlAttrName, attrs[name]!);
                }
            });
        }
    }

    private createFrame(attrs?: FrameAttributes) {
        const frame: HTMLIFrameElement = document.createElement('iframe');

        const allowAttr = getAllow(attrs?.allow);
        const sandboxAttr = getSandbox(attrs?.sandbox);

        const frameAttrs: FrameAttributes = {
            ...attrs,
            allow: allowAttr,
            sandbox: sandboxAttr,
        };

        this.createAttributes(frame, frameAttrs);

        frame.src = this.frameUrl;

        return frame;
    }

    private onPaymentMessage(message: FrameMessageV1 | FrameMessageV2) {
        this.emit(
            message.type as EventKey<PaymentFrameEvents>,
            message as PaymentFrameEvents[EventKey<PaymentFrameEvents>],
        );

        if (message.type === FrameMessageType.external3ds) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            window.taxiApp
                ? openLinkInGo({
                      url: message.data.redirectUrl,
                      shouldOpenInSystemBrowser: message.data.navigationMode === 'universal_link',
                  })
                : openLink(message.data.redirectUrl);
        }
    }

    /**
     * Opens a link via requestOpenUri, GO app API
     * @see https://wiki.yandex-team.ru/eda/dev/web/superapp/protokol-obshhenija-taxapp-edawebview/?search=requestOpenUri
     *
     * If taxiApp is unavailable in the current window, an error is thrown
     * @param url
     * @param shouldOpenInSystemBrowser If false is sent for an http link, it will open the link inside the webview within the application.
     *                                  For non-http links, the flag value is ignored. If the flag is absent, the link will open in an external browser
     */
    public openLinkInGo(url: string, shouldOpenInSystemBrowser: boolean) {
        openLinkInGo({
            url,
            shouldOpenInSystemBrowser,
        });
    }
}
